import type { ActivityItem } from '../../Services/activity';

// Catalogue de rendu du flux d'activité, version ADMIN.
//
// ⚠️ DUPLICATION ASSUMÉE de apps/ui-partners/app/src/activity/labels.ts.
// Il n'existe aucun package UI partagé dans ce monorepo, et ui-admin est en CRA
// (webpack ne compile pas de TS hors de src/ sans éjecter la config) — un module
// commun coûterait plus cher que le problème qu'il résout.
//
// Ce qui rend cette duplication SUPPORTABLE : le mécanisme de repli. Si les deux
// catalogues divergent, l'app en retard affiche `fallbackText` (le libellé brut servi
// par le back) — donc une phrase moins jolie, jamais une information fausse ni un
// écran vide. C'est le seul type de duplication que je m'autorise ici.
//
// ⚠️ En ajoutant un event, mettre à jour les DEUX fichiers. Le fichier de référence
// reste celui de ui-partners (c'est la surface client-facing).

export const activityVerbs: Record<string, string> = {
	'user.signed_up': "s'est inscrit",
	'user.onboarding_completed': 'a terminé son onboarding',
	'project.created': 'a créé le projet {object}',
	// Deux faits distincts : l'invitation part vs elle est acceptée. Ne pas les fondre
	// en « a ajouté X » — le flux ne doit pas affirmer une adhésion qui n'a pas eu lieu.
	'project.member_invited': 'a invité {object} au projet',
	'project.member_joined': 'a rejoint le projet {object}',
	'connector.configured': 'a configuré le connecteur {object}',
	'connector.deleted': 'a supprimé le connecteur {object}',
	// « synchronisation automatique » et pas « connecteur » : le connecteur reste
	// configuré, c'est le cron qui est mis en pause / relancé.
	'connector.enabled': 'a activé la synchronisation automatique de {object}',
	'connector.disabled': 'a mis en pause la synchronisation automatique de {object}',
	'connector.credentials_updated': 'a mis à jour les identifiants du connecteur {object}',
	'llm.session_started': 'a lancé une analyse via {object}',
	'dashboard.created': 'a créé le dashboard {object}',
	'connector.beta_requested': 'a demandé l’accès beta à {object}',
	'connector.sync_triggered': 'a lancé une synchronisation de {object}',
	'partner.organisation_created': 'a créé l’organisation {object}',
	'partner.opportunity_submitted': 'a soumis le lead {object}',
	'partner.client_created': 'a créé le client {object}',
	'partner.payout_requested': 'a demandé un déblocage de {object}',
	'partner.member_invited': 'a invité {object} dans l’organisation',
	'partner.member_joined': 'a rejoint l’organisation {object}',
};

// Phrases IMPERSONNELLES pour les events sans acteur humain (facturation, synchro) :
// « Facture réglée » est juste, « Synchronisation a réglé une facture » est absurde.
export const activityImpersonalVerbs: Record<string, string> = {
	'billing_account.created': 'Compte de facturation ouvert : {object}',
	// ⚠️ Doublon VOLONTAIRE avec la phrase personnelle ci-dessus, à ne pas supprimer :
	// l'émetteur ne publie l'ID numérique que depuis 2026-08-07. Les events antérieurs
	// n'ont pas d'acteur et s'afficheraient « Synchronisation a demandé… » sans ça.
	'connector.beta_requested': 'Demande d’accès beta : {object}',
	'subscription.created': 'Abonnement souscrit : {object}',
	'subscription.upgraded': "Changement d'offre : {object}",
	'subscription.cancelled': 'Abonnement résilié',
	'payment.succeeded': 'Facture réglée',
	'payment.failed': 'Paiement en échec',
};

const MACHINE_ACTOR = 'Synchronisation';
const UNKNOWN_ACTOR = 'Un utilisateur';

// #region renderActivityParts
// renderActivityParts produit la phrase affichée EN DEUX MORCEAUX : le nom de l'acteur
// d'un côté, le reste de la phrase de l'autre. C'est ce qui permet à l'admin de rendre
// le seul nom cliquable (vers la fiche utilisateur) sans transformer toute la ligne en
// lien — et sans reconstruire la phrase à coups de découpe de chaîne.
//
// `actor` vaut null pour les phrases IMPERSONNELLES (facturation, synchro) : il n'y a
// alors personne vers qui pointer.
//
// Aucune exception possible : on retombe toujours sur quelque chose de lisible.
export const renderActivityParts = (item: ActivityItem): { actor: string | null; text: string } => {
	const object = item.object.label || item.object.id || '';

	// Sans acteur humain, on cherche d'abord une phrase impersonnelle.
	if (!item.actor.label) {
		const impersonal = activityImpersonalVerbs[item.type];
		if (impersonal && !(impersonal.includes('{object}') && !object)) {
			return { actor: null, text: impersonal.replace('{object}', object) };
		}
	}

	const actor = item.actor.label || (item.isMachine ? MACHINE_ACTOR : UNKNOWN_ACTOR);
	const template = activityVerbs[item.type];

	// Type inconnu du front, OU gabarit qui attend un objet qu'on n'a pas : on préfère
	// le libellé brut à une phrase tronquée (« a créé le projet »).
	if (!template || (template.includes('{object}') && !object)) {
		return { actor, text: `— ${item.fallbackText || item.type}` };
	}

	return { actor, text: template.replace('{object}', object) };
};
// #endregion

// #region renderActivity
// renderActivity — la même phrase, d'un seul tenant, pour les surfaces qui n'ont pas
// besoin de rendre l'acteur cliquable (fiche utilisateur : le lien pointerait sur la
// page où l'on se trouve déjà).
export const renderActivity = (item: ActivityItem): string => {
	const { actor, text } = renderActivityParts(item);
	return actor ? `${actor} ${text}` : text;
};
// #endregion

// #region typeLabel
// typeLabel — libellé court d'un type, pour le filtre.
export const typeLabel = (type: string): string => {
	const template = activityVerbs[type] || activityImpersonalVerbs[type];
	if (!template) return type.replace(/[._]/g, ' ');
	return template.replace('{object}', '…');
};
// #endregion
